<script setup lang="ts">
import { computed, watch } from "vue";

import { DictsysInflectionSchemeHelper } from "@/helpers";
import type { DictsysArticle } from "@/types";
import type { JsonArticleComponentProps } from "@/types/components/json-article-component.type";

import LeArticleBody from "./LeArticleBody.vue";
import LeArticleHeader from "./LeArticleHeader.vue";
import LeArticlePrefix from "./LeArticlePrefix.vue";

const {
  article,
  isCompactArticleExpanded = false,
  searchViewType
} = defineProps<JsonArticleComponentProps<DictsysArticle>>();

const isCompactSearchView = computed<boolean>(() => searchViewType === "compact");

// Unwrap rawJson inflection schemes
watch(
  () => article.id,
  () => {
    const data = article.article;
    DictsysInflectionSchemeHelper.parseJsonInflectionScheme(data.inflectionScheme);

    data.meanings.forEach((meaning) => {
      meaning.translations.forEach((translation) => {
        DictsysInflectionSchemeHelper.parseJsonInflectionScheme(translation.inflectionScheme);
      });

      meaning.combinations.forEach((combination) => {
        DictsysInflectionSchemeHelper.parseJsonInflectionScheme(combination.inflectionScheme);
      });
    });
  },
  { immediate: true }
);
</script>

<template>
  <article class="le-article">
    <LeArticlePrefix :article="article" />
    <LeArticleHeader
      class="le-article__header"
      :article="article"
      :force-inline-buttons="isCompactSearchView"
      :with-dictionary-cover="isCompactSearchView"
    >
      <template #feedbackButton>
        <slot name="feedbackButton" />
      </template>
      <template #aboutDictionaryButton>
        <slot name="aboutDictionaryButton" />
      </template>
    </LeArticleHeader>
    <LeArticleBody :article="article" :collapsible="isCompactSearchView" :expanded="isCompactArticleExpanded">
      <template #compactExpandButton="slotProps">
        <slot v-if="isCompactSearchView" name="compactExpandButton" v-bind="slotProps" />
      </template>
    </LeArticleBody>
    <slot v-if="isCompactSearchView" name="compactFooter" />
  </article>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";
@import "../style/le-article";
</style>
